import { Box, TextField, type TextFieldProps, Typography } from '@mui/material';
import { forwardRef } from 'react';

import { getTimezoneAbbreviation } from 'helpers/datetime';

import { useTimezoneContext } from 'components/timezones';

type TimezoneTextFieldProps = TextFieldProps & {
  placeholderProp?: string;
  showCurrentTimeZone?: boolean;
  timezoneProp?: string;
};

const TimezoneTextField = forwardRef<HTMLDivElement, TimezoneTextFieldProps>(
  (
    {
      placeholderProp: placeholder,
      showCurrentTimeZone,
      timezoneProp: timezone,
      value,
      ...props
    }: TimezoneTextFieldProps,
    ref,
  ) => {
    const { currentTimeZoneAbbreviation: contextTimezoneAbbr } =
      useTimezoneContext();
    const timezoneAbbreviation = timezone
      ? getTimezoneAbbreviation(timezone)
      : contextTimezoneAbbr;

    const hasValue = Boolean(value) && value !== props.placeholder;
    const displayValue = placeholder
      ? `${placeholder}${hasValue ? ` (${value})` : ''}`
      : value;

    return (
      <TextField
        {...props}
        inputProps={{
          ...props.inputProps,
          ...(placeholder && { readOnly: true, value: displayValue }),
        }}
        InputProps={{
          ...props.InputProps,
          endAdornment: (
            <Box alignItems="center" display="flex" gap={0}>
              {showCurrentTimeZone && (
                <Typography
                  fontStyle="italic"
                  variant="caption"
                  whiteSpace="nowrap"
                >
                  {timezoneAbbreviation}
                </Typography>
              )}

              {props.InputProps?.endAdornment}
            </Box>
          ),
        }}
        ref={ref}
        value={displayValue}
      />
    );
  },
);

export default TimezoneTextField;
